/**
 * Glass blocks: a prism, a rectangular slab and a semicircular block.
 *
 * These are REAL surfaces — Snell's law at every face, total internal
 * reflection where there is no refracted ray — because deviation, lateral
 * shift and the critical angle are the whole point of them.
 */
import { PartKind, numbered } from '../registry';
import { localToWorld, solidSurfaces } from '../shapes';
import { Fact, Part, Surface, Vec2, World } from '../types';
import { indexAt } from '../trace';
import { deg, toWorld } from '../vec';
import { WHITE_LIGHT } from './sources';

export type Dispersion = 'none' | 'low' | 'high';
export interface PrismData {
    apex: number;
    /** The index at 589 nm, as typed. */
    n: number;
    side: number;
    /** How far the other colours' indices spread round `n`. Absent on a prism
     *  saved before prisms dispersed, which then does not. */
    dispersion?: Dispersion;
    [key: string]: unknown;
}

/**
 * Cauchy B, nm², for each setting. Low is crown glass (BK7's B is about
 * 4200 nm²): some 1.3° between red and violet through a 60° prism of n = 1.5,
 * a spread a student could miss. High is a flint-like spread, about 3.5°, so
 * the spectrum is plain to see. Neither moves `n` itself.
 */
export const DISPERSION_B: Record<Dispersion, number> = { none: 0, low: 4200, high: 11000 };

export const dispersionOf = (d: PrismData): number => DISPERSION_B[d.dispersion ?? 'none'] ?? 0;
export interface SlabData { width: number; thickness: number; n: number; [key: string]: unknown }
export interface SemicircleData { radius: number; n: number; [key: string]: unknown }

const nControl = { type: 'number' as const, key: 'n', label: 'Refractive index n', min: 1, max: 2.5, step: 0.01 };

/** sin C = n_outside / n_inside, from the denser side. */
export function criticalAngle(n: number, medium: number): number | null {
    return n > medium ? deg(Math.asin(medium / n)) : null;
}

const criticalFact = (n: number, w: World): Fact => {
    const c = criticalAngle(n, w.medium);
    return { label: 'Critical angle', value: c === null ? 'none: not denser than its surroundings' : `${c.toFixed(1)}°` };
};

/** Apex up, anticlockwise from the apex, centred on its centroid. */
function prismCorners(d: PrismData): Vec2[] {
    const half = (d.apex * Math.PI) / 360;
    const a = d.side * Math.sin(half), b = d.side * Math.cos(half);
    const pts = [{ x: 0, y: 0 }, { x: -a, y: -b }, { x: a, y: -b }];
    const cy = -(2 * b) / 3;
    return pts.map(p => ({ x: p.x, y: p.y - cy }));
}

export const prism: PartKind<PrismData> = {
    key: 'prism',
    label: 'Prism',
    tag: 'P',
    freedom: { onAxis: false, turns: true },
    create: (at: Vec2, id: string): Part<PrismData> => ({
        id, kind: 'prism', at, angle: 0, mount: 'free', data: { apex: 60, n: 1.5, side: 8, dispersion: 'high' },
    }),
    // apex→left base is the face light usually meets first; then the base; then the other face.
    surfaces: (p, w) => solidSurfaces(p as Part, w, prismCorners(p.data), p.data.n,
        ['the first face', 'the base', 'the second face'], numbered(p as Part, w, 'P'), dispersionOf(p.data)),
    outline: (p) => localToWorld(p as Part, prismCorners(p.data)),
    controls: () => [
        { type: 'number', key: 'apex', label: 'Angle of the prism A', unit: '°', min: 10, max: 90, step: 1 },
        { ...nControl, label: 'Refractive index n (yellow, 589 nm)' },
        {
            type: 'choice', key: 'dispersion', label: 'Dispersion',
            choices: [{ value: 'none', label: 'none' }, { value: 'low', label: 'low' }, { value: 'high', label: 'high' }],
        },
        { type: 'number', key: 'side', label: 'Side', unit: 'cm', min: 3, max: 20, step: 0.5 },
    ],
    keyFacts(p, w) {
        const facts = [criticalFact(p.data.n, w)];
        const B = dispersionOf(p.data);
        if (B) {
            const [red, , violet] = WHITE_LIGHT;
            facts.push({ label: 'Index for red · violet', value: `${indexAt(p.data.n, B, red).toFixed(3)} · ${indexAt(p.data.n, B, violet).toFixed(3)}` });
        }
        return facts;
    },
    name: (p, w) => numbered(p as Part, w, 'P'),
};

const slabCorners = (d: SlabData): Vec2[] => {
    const x = d.width / 2, y = d.thickness / 2;
    return [{ x: -x, y: -y }, { x, y: -y }, { x, y }, { x: -x, y }];
};

export const slab: PartKind<SlabData> = {
    key: 'slab',
    label: 'Rectangular block',
    tag: 'G',
    freedom: { onAxis: false, turns: true },
    create: (at: Vec2, id: string): Part<SlabData> => ({
        id, kind: 'slab', at, angle: 0, mount: 'free', data: { width: 8, thickness: 4, n: 1.5 },
    }),
    surfaces: (p, w) => solidSurfaces(p as Part, w, slabCorners(p.data), p.data.n,
        ['the bottom face', 'the right face', 'the top face', 'the left face'], numbered(p as Part, w, 'G')),
    outline: (p) => localToWorld(p as Part, slabCorners(p.data)),
    controls: () => [
        { type: 'number', key: 'width', label: 'Width', unit: 'cm', min: 1, max: 30, step: 0.5 },
        { type: 'number', key: 'thickness', label: 'Thickness t', unit: 'cm', min: 1, max: 30, step: 0.5 },
        nControl,
    ],
    keyFacts: (p, w) => [criticalFact(p.data.n, w)],
    name: (p, w) => numbered(p as Part, w, 'G'),
};

/** `at` is the centre of the flat face; the curve is on the side the part faces +90°. */
export const semicircle: PartKind<SemicircleData> = {
    key: 'semicircle',
    label: 'Semicircular block',
    tag: 'D',
    freedom: { onAxis: false, turns: true },
    create: (at: Vec2, id: string): Part<SemicircleData> => ({
        id, kind: 'semicircle', at, angle: 0, mount: 'free', data: { radius: 5, n: 1.5 },
    }),
    surfaces(p, w): Surface[] {
        const r = p.data.radius;
        const name = numbered(p as Part, w, 'D');
        const [flat] = solidSurfaces(p as Part, w, [{ x: -r, y: 0 }, { x: r, y: 0 }], p.data.n, ['the flat face'], name);
        return [
            flat,
            {
                id: `${p.id}:curve`, partId: p.id,
                shape: { form: 'arc', centre: p.at, radius: r, from: p.angle, to: p.angle + Math.PI },
                rule: { law: 'refract', outside: w.medium, inside: p.data.n },
                name: `the curved face of ${name}`,
            },
        ];
    },
    outline(p) {
        const r = p.data.radius;
        const arc = Array.from({ length: 33 }, (_, i) => {
            const t = (Math.PI * i) / 32;
            return { x: r * Math.cos(t), y: r * Math.sin(t) };
        });
        return arc.map(q => toWorld(p.at, p.angle, q));
    },
    controls: () => [
        { type: 'number', key: 'radius', label: 'Radius', unit: 'cm', min: 2, max: 15, step: 0.5 },
        nControl,
    ],
    keyFacts: (p, w) => [criticalFact(p.data.n, w)],
    name: (p, w) => numbered(p as Part, w, 'D'),
};
